import { Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { LucideAngularModule, KeyRound, LoaderCircle } from 'lucide-angular';
import { AuthService } from '@core/api/auth/auth.service';
import { ToastService } from '@shared/components/toast/toast.service';
import { ModalService } from '@shared/components/modal/modal.service';

@Component({
    selector: 'app-password-change-reminder-modal',
    standalone: true,
    imports: [LucideAngularModule],
    template: `
        <div class="text-center py-5">
            <lucide-icon
                [img]="KeyRound"
                [size]="64"
                [strokeWidth]="2"
                class="mx-auto mb-6 text-yellow-600"
            ></lucide-icon>
            <h3 class="text-lg font-semibold text-gray-900 mb-3">Password Change Required</h3>
            <p class="text-gray-700 text-base leading-relaxed mb-6">
                For security purposes, you need to update your password. You can change it now or waive this requirement
                for later.
            </p>
            <div class="flex gap-3 justify-center">
                <button class="button button--secondary" (click)="waivePasswordChange()" [disabled]="isWaiving">
                    @if (isWaiving) {
                    <lucide-icon [img]="LoaderCircle" class="w-5 h-5 inline-block mr-2 animate-spin"> </lucide-icon>
                    }
                    {{ isWaiving ? 'Waiving...' : 'Waive Change' }}
                </button>
                <button class="button button--primary" (click)="goToChangePassword()">Change Password</button>
            </div>
        </div>
    `,
})
export class PasswordChangeReminderModalComponent {
    readonly KeyRound = KeyRound;
    readonly LoaderCircle = LoaderCircle;
    isWaiving = false;

    private router = inject(Router);
    private authService = inject(AuthService);
    private toastService = inject(ToastService);
    private modalService = inject(ModalService);

    waivePasswordChange(): void {
        this.isWaiving = true;
        this.authService.waivePasswordChange().subscribe({
            next: (response) => {
                this.isWaiving = false;
                if (response.status === 'success') {
                    // Refresh user data to get updated password_change_waived_until
                    this.authService.getCurrentUser().subscribe({
                        next: (userResponse) => {
                            this.authService.setUser(userResponse.data!);
                            this.toastService.show({
                                title: 'Password Change Waived',
                                content: response.message || 'Password change waived successfully',
                                type: 'success',
                                duration: 5000,
                            });
                            this.closeModal();
                        },
                        error: () => {
                            this.toastService.show({
                                content: 'Failed to refresh user data',
                                type: 'error',
                            });
                            this.closeModal();
                        },
                    });
                }
            },
            error: (error) => {
                this.isWaiving = false;
                this.toastService.show({
                    title: 'Failed to Waive Password Change',
                    content: error?.error?.message || 'Failed to waive password change',
                    type: 'error',
                    duration: 5000,
                });
            },
        });
    }

    goToChangePassword(): void {
        this.closeModal();
        this.router.navigate(['/change-password']);
    }

    private closeModal(): void {
        const currentModals = this.modalService.currentModals;
        if (currentModals.length > 0) {
            this.modalService.close(currentModals[currentModals.length - 1].id);
        }
    }
}
